import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useParams, useHistory } from "react-router-dom";
import {
  Grid,
  Paper,
  Typography,
  Divider,
  Box,
  Tooltip,
  IconButton,
  Button,
  Card,
  CardContent,
  Chip,
  useTheme,
  alpha,
  Badge,
  Alert,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import TableItem from "../../../components/TableItem";
import {
  getSaleBillById,
  getSaleBillPaymentsById,
  updateRemainingAmount,
  updateSaleBillPayments,
} from "../../../services/Sales";
import CloseIcon from "@mui/icons-material/Close";
import CustomerDataComponent from "../../../components/CustomerDataComponent";
import dayjs from "dayjs";
import PaymentOptions from "../../../components/PaymentOptions";
import FormAlert from "../../../components/FormAlert";
import InputField from "../../../FormComponents/InputField";

// Icons
import ReceiptIcon from "@mui/icons-material/Receipt";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PersonIcon from "@mui/icons-material/Person";
import DescriptionIcon from "@mui/icons-material/Description";
import DiscountIcon from "@mui/icons-material/Discount";
import SaveIcon from "@mui/icons-material/Save";
import TagIcon from "@mui/icons-material/Tag";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import StorefrontIcon from "@mui/icons-material/Storefront";
import PaymentIcon from "@mui/icons-material/Payment";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";

function SaleBillUpdatePayments() {
  const theme = useTheme();
  const history = useHistory();
  const { billId } = useParams();
  const [saleBill, setSaleBill] = useState(null);
  const [rows, setRows] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [paymentMethods, setPaymentMethods] = useState(null);
  const [displayAlert, setAlert] = useState(false);
  const [alertData, setAlertData] = useState({
    type: "",
    message: "",
  });

  useEffect(() => {
    handleRemainingAmounts();
  }, [paymentMethods]);

  const handleRemainingAmounts = () => {
    if (paymentMethods && saleBill) {
      const remainingValue = paymentMethods.reduce(
        (sum, payment) => sum + parseFloat(payment.amount || 0),
        0
      );
      const updatedRemainingValue =
        parseFloat(saleBill.totalPrice || 0) - remainingValue;
      setSaleBill({
        ...saleBill,
        remainingAmount: updatedRemainingValue,
      });
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const billResult = await getSaleBillById(billId);
      const billPaymentResult = await getSaleBillPaymentsById(billId);

      if (billResult !== "Something went wrong...") {
        setSaleBill(billResult);
        const billItemSet = billResult.billItems || [];

        const itemRows = billItemSet.map((item) => ({
          productName: item.product.name,
          barcode: item.product.barcode,
          sellingQty: item.bill_qty,
          totalProfit: item.itemProfit,
          profitMargin: item.pricePerItem,
          unitPrice: item.pricePerItem,
          totalPrice: item.pricePerItem * item.bill_qty,
          action: item.product.product_id,
        }));

        setRows(itemRows);
      } else {
        setErrors({
          ...errors,
          _billDataError: "Unable to fetch bill data",
        });
      }

      if (billPaymentResult !== "Something went wrong...") {
        const billPayments = billPaymentResult.map((p) => ({
          type: p.paymentType,
          amount: p.amount,
          bank: p.bank,
          chequeNumber: p.chequeNumber,
          returnDate: p.returnDate,
          returnDateInput: p.returnDate != null ? dayjs(p.returnDate) : null,
          returnBillNumber: p.returnBillNumber,
        }));
        setPaymentMethods(billPayments);
      } else {
        setErrors({
          ...errors,
          _billPaymentError: "Unable to fetch bill payment data",
        });
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [billId]);

  const columns = [
    {
      id: "productName",
      label: "Product Name",
      minWidth: 150,
      editable: false,
    },
    {
      id: "barcode",
      label: "Barcode",
      minWidth: 120,
      editable: false,
    },
    {
      id: "sellingQty",
      label: "Qty",
      minWidth: 80,
      editable: false,
      type: "number",
      isDecimal: false,
    },
    {
      id: "unitPrice",
      label: "Unit Price",
      minWidth: 100,
      editable: false,
      type: "number",
      isDecimal: false,
      format: (value) => `Rs: ${parseFloat(value).toFixed(2)}`,
    },
    {
      id: "totalPrice",
      label: "Total Price",
      minWidth: 120,
      editable: false,
      type: "number",
      isDecimal: false,
      format: (value) => `Rs: ${parseFloat(value).toFixed(2)}`,
    },
    {
      id: "totalProfit",
      label: "Total Profit",
      minWidth: 100,
      editable: false,
      type: "number",
      isDecimal: false,
      format: (value) => `Rs: ${parseFloat(value).toFixed(2)}`,
    },
    {
      id: "profitMargin",
      label: "Profit Margin",
      minWidth: 100,
      editable: false,
      type: "number",
      isDecimal: false,
    },
    { id: "action", label: "Actions", minWidth: 80 },
  ];

  const handleAction = async (event, id) => {
    history.push(`/template/sale_bill_item_view/${id}/${saleBill.billNumber}`);
  };

  const submitValue = async () => {
    if (paymentMethods && paymentMethods.length > 0) {
      try {
        console.log("Submitting payment methods:", paymentMethods);
        const submitBillPayment = await updateSaleBillPayments(
          billId,
          paymentMethods
        );
        const updateBillRemainingAmount = await updateRemainingAmount(saleBill);

        if (
          submitBillPayment &&
          updateBillRemainingAmount !== "Something went wrong..."
        ) {
          history.push("/template/sale_bill_view/" + billId);
        } else {
          setAlertData({
            type: "error",
            message: "Something went wrong...",
          });
          setAlert(true);
        }
      } catch (error) {
        console.error("Error submitting payment:", error);
        setAlertData({
          type: "error",
          message: "Failed to update payment information",
        });
        setAlert(true);
      }
    }
  };

  const formatCurrency = (value) => {
    return typeof value === "number"
      ? `Rs: ${value.toFixed(2)}`
      : `Rs: ${parseFloat(value || 0).toFixed(2)}`;
  };

  const getStatusChip = (status) => {
    let color;
    let bgColor;

    switch (status?.toUpperCase()) {
      case "PAID":
        color = theme.palette.success.main;
        bgColor = alpha(theme.palette.success.main, 0.1);
        break;
      case "PARTIAL":
        color = theme.palette.warning.main;
        bgColor = alpha(theme.palette.warning.main, 0.1);
        break;
      case "UNPAID":
        color = theme.palette.error.main;
        bgColor = alpha(theme.palette.error.main, 0.1);
        break;
      default:
        color = theme.palette.grey[600];
        bgColor = alpha(theme.palette.grey[600], 0.1);
    }

    return (
      <Chip
        label={status || "Unknown"}
        size="small"
        sx={{
          bgcolor: bgColor,
          color: color,
          fontWeight: 600,
          borderRadius: 1,
          px: 1,
        }}
      />
    );
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography color="text.secondary">Loading bill details...</Typography>
      </Box>
    );
  }

  if (!saleBill) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 4,
          m: 3,
          textAlign: "center",
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.error.light, 0.1),
        }}
      >
        <Box sx={{ mb: 2 }}>
          <DescriptionIcon color="error" sx={{ fontSize: 48 }} />
        </Box>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Bill Not Found
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          The requested bill data could not be retrieved or does not exist.
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => history.goBack()}
        >
          Go Back
        </Button>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 2, md: 2 },
        m: { xs: 1, md: 2 },
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <PaymentIcon fontSize="large" color="primary" />
          <Typography variant="h5" fontWeight="600" color="primary.main">
            Update Sale Bill Payments
          </Typography>
        </Box>
        <Tooltip title="Close">
          <IconButton
            onClick={() => history.goBack()}
            aria-label="close"
            sx={{
              bgcolor: alpha(theme.palette.grey[500], 0.1),
              "&:hover": {
                bgcolor: alpha(theme.palette.grey[500], 0.2),
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Bill Header Information */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ height: "100%", borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <TagIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color="primary.main"
                >
                  Bill Information
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Bill Number
                  </Typography>
                  <Typography variant="body1" fontWeight="600">
                    {saleBill.billNumber}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Date
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <CalendarTodayIcon
                      sx={{ fontSize: 16, color: "text.secondary" }}
                    />
                    <Typography variant="body1">
                      {new Date(saleBill.sellingDate).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Added By
                  </Typography>
                  <Typography variant="body1">{saleBill.addedBy}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Paid Status
                  </Typography>
                  {getStatusChip(saleBill.paidStatus)}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ height: "100%", borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <PersonIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color="primary.main"
                >
                  Customer Information
                </Typography>
              </Box>

              {saleBill.customer ? (
                <CustomerDataComponent customer={saleBill.customer} />
              ) : (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.grey[500], 0.05),
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Typography color="text.secondary">
                    No customer assigned to this bill
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Financial Information */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <Card elevation={1} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <AttachMoneyIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color="primary.main"
                >
                  Financial Summary
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Total Amount
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight="700"
                      color="success.main"
                    >
                      {formatCurrency(saleBill.totalPrice)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Total Profit
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight="700"
                      color="primary.main"
                    >
                      {formatCurrency(saleBill.totalProfit)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ p: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Discount
                    </Typography>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <DiscountIcon
                        sx={{ fontSize: 16, color: "warning.main" }}
                      />
                      <Typography variant="body1" fontWeight="500">
                        {saleBill.discount_percentage > 0.0
                          ? `${saleBill.discount_percentage}%`
                          : formatCurrency(saleBill.discount_amount)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Remaining Amount
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight="700"
                      color={
                        parseFloat(saleBill.remainingAmount) > 0
                          ? "warning.main"
                          : "success.main"
                      }
                    >
                      {formatCurrency(saleBill.remainingAmount)}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Bill Note */}
      {saleBill.note && (
        <Card elevation={1} sx={{ mb: 4, borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <DescriptionIcon sx={{ mr: 1, color: "primary.main" }} />
              <Typography
                variant="subtitle1"
                fontWeight="700"
                color="primary.main"
              >
                Note
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ pl: 3, mt: 1 }}>
              {saleBill.note}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Bill Items Table */}
      <Card elevation={1} sx={{ mb: 4, borderRadius: 2, overflow: "hidden" }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <StorefrontIcon sx={{ mr: 1, color: "primary.main" }} />
            <Typography
              variant="subtitle1"
              fontWeight="700"
              color="primary.main"
            >
              Bill Items
            </Typography>
            <Badge badgeContent={rows.length} color="primary" sx={{ ml: 1 }} />
          </Box>

          <Box
            sx={{
              bgcolor: alpha(theme.palette.background.default, 0.5),
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            <TableItem
              columns={columns}
              rows={rows}
              page={0}
              setPage={() => {}}
              tablePagin={false}
              totalPages={1}
              getData={fetchData}
              showActions={{
                view: "Bill item view",
              }}
              actionRoles={{
                view: ["SALE_BILL_PAYMENT_UPDATE", "ROLE_ADMIN"],
              }}
              handleAction={handleAction}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Payment Options */}
      <Card elevation={1} sx={{ mb: 4, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <AccountBalanceIcon sx={{ mr: 1, color: "primary.main" }} />
            <Typography
              variant="subtitle1"
              fontWeight="700"
              color="primary.main"
            >
              Payment Information
            </Typography>
          </Box>
          <PaymentOptions
            paymentMethods={paymentMethods}
            setPaymentMethods={setPaymentMethods}
            billType={"SALE"}
            paymentTypes={[
              { value: "CASH", label: "Cash" },
              { value: "CHEQUE", label: "Cheque" },
              { value: "BILL", label: "Recover Import Bill" },
              { value: "RECEIPT", label: "Receipt" },
            ]}
          />
        </CardContent>
      </Card>

      {/* Alert and Submit Section */}
      <Card elevation={1} sx={{ borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            {displayAlert && (
              <Grid item xs={12}>
                <Alert
                  severity={alertData.type}
                  onClose={() => setAlert(false)}
                  sx={{ mb: 2 }}
                >
                  {alertData.message}
                </Alert>
              </Grid>
            )}
            <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => history.goBack()}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={submitValue}
                disabled={!paymentMethods || paymentMethods.length === 0}
                sx={{ px: 3 }}
              >
                Update Payments
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Paper>
  );
}

SaleBillUpdatePayments.propTypes = {
  billId: PropTypes.string,
};

export default SaleBillUpdatePayments;
